import React, { useContext, useState, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import axios from 'axios';
import { IModifierTemplateItem, IGetModifierGroupInfoV2, ServerErrorTypes } from 'types';
import { getModifierTemplates, updateModifierTemplates, getModifierGroupInfoV2 } from 'backend/modifierGroups';
import { ModalContent } from 'components/ModalWindows/ModalContent';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Text from 'components/Text/Text';
import { ModifiersMultipleSelect } from './ModifiersMultipleSelect';
import style from './index.module.scss';

interface IProps {
	onCloseModal: () => void;
	onFailureEvent?: () => void;
	onShowError: () => void;
	onShowProductHasPortionsError: () => void;
	modifierGroupId: string;
	customerId: string;
}

export const ModalEditModifierTemplate: React.FC<IProps> = ({
	onCloseModal,
	onFailureEvent,
	onShowError,
	onShowProductHasPortionsError,
	modifierGroupId,
	customerId,
}) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [modifiers, setModifiers] = useState<string[]>([]);
	const [modifiersWithAdditionalData, setModifiersWithAdditionalData] = useState<IModifierTemplateItem[]>([]);
	const [modifierGroupInfoData, setModifierGroupInfoData] = useState<Nullable<IGetModifierGroupInfoV2>>(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				showLoader();
				const modifierTemplatesResponse = await getModifierTemplates(modifierGroupId);
				const modifierIds = modifierTemplatesResponse.data.modifiers.map(
					(modifier: IModifierTemplateItem) => modifier.id,
				);
				setModifiers(modifierIds);
				setModifiersWithAdditionalData(modifierTemplatesResponse.data.modifiers);

				const modifierGroupInfoResponse = await getModifierGroupInfoV2(modifierGroupId);
				setModifierGroupInfoData(modifierGroupInfoResponse);
			} catch (err) {
				if (axios.isAxiosError(err) && err.response?.data.Type === ServerErrorTypes.PRODUCT_HAS_PORTIONS) {
					onShowProductHasPortionsError();
				} else {
					onShowError();
				}
			} finally {
				hideLoader();
			}
		};
		fetchData();
	}, []);

	const handleAssignTemplates = async () => {
		try {
			showLoader();
			await updateModifierTemplates({
				modifierGroupId,
				modifierIds: modifiersWithAdditionalData.map((m) => m.id),
			});
			onCloseModal();
		} catch (err) {
			onShowError();
		} finally {
			hideLoader();
		}
	};

	return (
		<>
			{modifierGroupInfoData && (
				<ModalContent
					onConsentEvent={handleAssignTemplates}
					onFailureEvent={onFailureEvent ? onFailureEvent : onCloseModal}
					heading="editTemplateForModifiers"
					headerTranslationNameSpace="modals"
					translationOfConfirmBtn="saveButton"
					headingAlignment="left"
					centered={false}>
					<Text translationKey="setModifierTemplateAndTheirPriority" translationNameSpace="modals" size="s" />
					<div className={style.row}>
						<div>
							<FullInfoField label="customerLabel" />
							<Text translationKey={modifierGroupInfoData?.customer.name} size="s" withTooltip={true} />
						</div>
						<div>
							<FullInfoField label="modifierGroupLabel" />
							<Text translationKey={modifierGroupInfoData?.name} size="s" withTooltip={true} />
						</div>
					</div>
					<ModifiersMultipleSelect
						setModifiers={setModifiers}
						setModifiersWithAdditionalData={setModifiersWithAdditionalData}
						modifiers={modifiers}
						modifiersWithAdditionalData={modifiersWithAdditionalData}
						customerId={customerId}
					/>
				</ModalContent>
			)}
		</>
	);
};
